/**
 * Shared sx fragments for onboarding custom buttons (`Box component="button"`
 * with `all: 'unset'`). The `all: 'unset'` reset strips the user-agent and
 * MUI focus rings, so each custom button needs to put one back, otherwise
 * keyboard users have no visible indication of focus.
 *
 * Spread this at the end of the button's sx so it wins over earlier rules.
 */

export const pillButtonFocusRing = {
  // Two-pixel outline in primary-40 with a small offset reads clearly against
  // both the brand-blue filled pills and the white outline pills used in the
  // onboarding flow.
  '&:focus-visible': {
    outline: '2px solid var(--color-primary-40, #1D47A4)',
    outlineOffset: '2px',
  },
};

// Shared geometry for the onboarding action pills (40 px tall, Roboto 14/20,
// 100 px radius). ProfileSetupStep and OrganizationPicker sit in the same
// /register flow, so they must render byte-identical Back / Continue buttons —
// factoring the styling here keeps them from drifting apart.
const onboardingPillBase = {
  all: 'unset',
  boxSizing: 'border-box',
  minHeight: '40px',
  paddingInline: '16px',
  borderRadius: '100px',
  fontFamily: "'Roboto', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  letterSpacing: '0.1px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  ...pillButtonFocusRing,
} as const;

/** Outline "Back" pill — stroke via inset box-shadow so the pill stays 40 px. */
export const onboardingBackPillSx = {
  ...onboardingPillBase,
  boxShadow: 'inset 0 0 0 1px var(--color-outline-variant, #CAC4D0)',
  color: 'text.secondary',
  '&:hover': { backgroundColor: 'rgba(73, 69, 79, 0.04)' },
  '&:disabled': { cursor: 'not-allowed', opacity: 0.6 },
} as const;

/** Filled brand-blue "Continue" pill. */
export const onboardingPrimaryPillSx = {
  ...onboardingPillBase,
  backgroundColor: '#4D69B9',
  color: '#FFFFFF',
  '&:hover': { backgroundColor: '#3D54A0' },
  '&:disabled': { cursor: 'not-allowed', opacity: 0.6 },
} as const;
